import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Zap, Palette, User, Wrench, Info, LogOut, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/layout/Sidebar.css';

const Sidebar = ({ 
  sections, 
  activeSection, 
  setActiveSection, 
  isProCalculator,
  setCalculatorVersion,
  sectionCompletionStatus = {},
  mobileMenuOpen = false,
  setMobileMenuOpen = () => {}
}) => {
  const location = useLocation();
  const { user, signOut, profile, proAccessLoading } = useAuth();
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const navigate = useNavigate();
  const [adminExpanded, setAdminExpanded] = useState(
    location.pathname.startsWith('/admin')
  );

  const closeMenu = () => setMobileMenuOpen(false);
  
  // Filter out the "all" section and "icingPlans" (icingPlans is rendered separately without a number)
  const filteredSections = sections ? sections.filter(section => section.id !== 'all' && section.id !== 'icingPlans') : [];
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const isActivePro = (sectionId) => {
    return isProCalculator && activeSection === sectionId;
  };
  
  return (
    <>
      {mobileMenuOpen && (
        <div className="sidebar-backdrop" onClick={closeMenu} />
      )}
      <div className={`sidebar${mobileMenuOpen ? ' sidebar-mobile-open' : ''}`}>
      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          <li className={`sidebar-menu-item ${isActive('/') ? 'active' : ''}`}>
            <Link to="/" className="sidebar-link" onClick={closeMenu}>
              <span className="sidebar-icon"><Home size={20} aria-hidden="true" /></span>
              <span className="sidebar-text">Quick Calculator</span>
            </Link>
          </li>
          
          {/* Pro Calculator Link - Always Visible */}
          <li className={`sidebar-menu-item ${isActive('/pro') ? 'active' : ''}`}>
            <Link to="/pro" className="sidebar-link">
              <span className="sidebar-icon"><Zap size={20} aria-hidden="true" /></span>
              <span className="sidebar-text">Pro Calculator</span>
            </Link>
            
            {/* Submenu only shown when actually on the Pro Calc page (anonymous included) */}
            {isProCalculator && filteredSections && (
              <ul className="sidebar-submenu">
                {filteredSections.map((section, index) => {
                  // Get completion status for this section
                  const status = sectionCompletionStatus && sectionCompletionStatus[section.id] ? sectionCompletionStatus[section.id] : 'neutral';
                  
                  return (
                    <li 
                      key={section.id}
                      className={`sidebar-submenu-item ${isActivePro(section.id) ? 'active' : ''}`}
                    >
                      <button 
                        className="sidebar-submenu-link"
                        onClick={() => { setActiveSection(section.id); closeMenu(); }}
                      >
                        <span 
                          className={`step-number status-${status}`}
                          data-status={status}
                          data-section={section.id}
                          style={{
                            backgroundColor: status === 'completed' ? '#4CAF50' : 
                                             status === 'incomplete' ? '#F44336' :
                                             status === 'warning'    ? '#F9A825' :
                                             '#444',
                            color: '#fff',
                            boxShadow: status === 'completed' ? '0 0 5px rgba(76, 175, 80, 0.7)' : 
                                       status === 'incomplete' ? '0 0 5px rgba(244, 67, 54, 0.7)' :
                                       status === 'warning'    ? '0 0 5px rgba(249, 168, 37, 0.7)' :
                                       'none'
                          }}
                        >
                          {index + 1}
                        </span>
                        <span className="sidebar-text">{section.label}</span>
                      </button>
                    </li>
                  );
                })}
                
                {/* Saved Plans - Project Management */}
                <li 
                  className={`sidebar-submenu-item no-number ${isActivePro('icingPlans') ? 'active' : ''}`}
                >
                  <button 
                    className="sidebar-submenu-link"
                    onClick={() => { setActiveSection('icingPlans'); closeMenu(); }}
                  >
                    <span className="sidebar-text">Saved Plans</span>
                  </button>
                </li>
              </ul>
            )}
          </li>
          
          {/* Color Mix Guide Link */}
          <li className={`sidebar-menu-item ${isActive('/color-mix') ? 'active' : ''}`}>
            <Link to="/color-mix" className="sidebar-link" onClick={closeMenu}>
              <span className="sidebar-icon"><Palette size={20} aria-hidden="true" /></span>
              <span className="sidebar-text">Color Mix AI</span>
              <span className="sidebar-beta-badge">Beta</span>
            </Link>
          </li>
          {/* Recommended Products and Tip Jar moved to the footer — see Footer.js.
              Keeping them off the main nav so Home/Pro Calculator aren't competing
              with off-site destinations for a first-time visitor's attention. */}
          
          {user && (
            <li className={`sidebar-menu-item sidebar-bottom sidebar-account-link`}>
              <Link to="/account" className="sidebar-link" onClick={closeMenu}>
                <span className="sidebar-icon"><User size={20} aria-hidden="true" /></span>
                <span className="sidebar-text">My Account</span>
              </Link>
            </li>
          )}
          
          {/* Admin-only: collapsible Admin Pages section */}
          {user && !proAccessLoading && profile?.is_admin && (
            <li className="sidebar-menu-item sidebar-admin-section">
              <button
                className={`sidebar-link sidebar-admin-toggle ${adminExpanded ? 'expanded' : ''}`}
                onClick={() => setAdminExpanded(e => !e)}
              >
                <span className="sidebar-icon"><Wrench size={20} aria-hidden="true" /></span>
                <span className="sidebar-text">Admin Pages</span>
                <span className="sidebar-expand-arrow">{adminExpanded ? '▾' : '▸'}</span>
              </button>
              {adminExpanded && (
                <ul className="sidebar-submenu sidebar-admin-submenu">
                  <li className={`sidebar-submenu-item ${isActive('/admin/feedback') ? 'active' : ''}`}>
                    <Link to="/admin/feedback" className="sidebar-submenu-link">
                      <span className="sidebar-text">Feedback Review</span>
                    </Link>
                  </li>
                  <li className={`sidebar-submenu-item ${isActive('/admin/userstats') ? 'active' : ''}`}>
                    <Link to="/admin/userstats" className="sidebar-submenu-link">
                      <span className="sidebar-text">User Statistics</span>
                    </Link>
                  </li>
                  <li className={`sidebar-submenu-item ${isActive('/admin/activity') ? 'active' : ''}`}>
                    <Link to="/admin/activity" className="sidebar-submenu-link">
                      <span className="sidebar-text">Activity Charts</span>
                    </Link>
                  </li>
                  <li className={`sidebar-submenu-item ${isActive('/admin/deletions') ? 'active' : ''}`}>
                    <Link to="/admin/deletions" className="sidebar-submenu-link">
                      <span className="sidebar-text">Deletion Requests</span>
                    </Link>
                  </li>
                  <li className={`sidebar-submenu-item ${isActive('/admin/settings') ? 'active' : ''}`}>
                    <Link to="/admin/settings" className="sidebar-submenu-link">
                      <span className="sidebar-text">Global Settings</span>
                    </Link>
                  </li>
                  <li className={`sidebar-submenu-item ${isActive('/admin/recommended-products') ? 'active' : ''}`}>
                    <Link to="/admin/recommended-products" className="sidebar-submenu-link">
                      <span className="sidebar-text">Recommended Products</span>
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          )}
          
          {/* About Us Page Link */}
          <li className={`sidebar-menu-item ${isActive('/about-us') ? 'active' : ''}`}>
            <Link to="/about-us" className="sidebar-link" onClick={closeMenu}>
              <span className="sidebar-icon"><Info size={20} aria-hidden="true" /></span>
              <span className="sidebar-text">About Us</span>
            </Link>
          </li>
          
          {/* Settings Link - Always Visible but could be conditional later */}
          {/* <li className="sidebar-menu-item">
            <Link to="/settings" className="sidebar-link">
              <span className="sidebar-icon">⚙️</span>
              <span className="sidebar-text">Settings</span>
            </Link>
          </li> */}
           
          {/* Conditional Bottom Links */}
          {user ? (
            <li className="sidebar-menu-item sidebar-bottom">
              <button 
                className="sidebar-link logout-link" 
                onClick={async () => {
                  closeMenu();
                  if (signOut) {
                    const { error } = await signOut();
                    if (error) {}
                  }
                }}
              >
                <span className="sidebar-icon"><LogOut size={20} aria-hidden="true" /></span>
                <span className="sidebar-text">Logout ({user.email})</span>
              </button>
            </li>
          ) : (
            <li className="sidebar-menu-item sidebar-bottom">
              <Link to="/login" className="sidebar-link" onClick={closeMenu}>
                <span className="sidebar-icon"><KeyRound size={20} aria-hidden="true" /></span>
                <span className="sidebar-text">Login / Sign Up</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </div>
    </>
  );
};

export default Sidebar;
